import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { getSessionEvents } from '@/db/session-events';
import type { SessionId, UserId } from '@/schemas';
import * as authStoreModule from './auth-store';
import { OperationQueue } from './operation-queue';
import {
  createRequireActiveSession,
  createSessionRecovery,
  computeUndoRedoAvailability,
} from './session-helpers';
import { createSessionActions } from './session-store/actions';
import type { SessionStore } from './session-store.types';

export type { SessionStore } from './session-store.types';

// Lazy getter - only access auth store when function is actually called
// This breaks the circular dependency chain at module initialization time
// Import auth store - using lazy initialization pattern to break circular dependency
// The store itself is not accessed until getCurrentUserId is called
function getCurrentUserId(): UserId | undefined {
  // Access the store lazily - by the time this is called, all stores are initialized
  return authStoreModule.useAuthStore.getState().userId;
}

export const useSessionStore = create<SessionStore>()(
  devtools(
    immer((set, get) => {
      const loadToken = { value: 0 };

      const requireActiveSession = createRequireActiveSession(get);
      const refreshSessionFromEvents = createSessionRecovery({ get, set });
      const operationQueue = new OperationQueue();
      operationQueue.setDiagnosticsListener((diagnostics) => {
        set((draft) => {
          draft.queueDiagnostics = diagnostics;
        });
      });

      const updateUndoRedoAvailability = async (sessionId: SessionId): Promise<void> => {
        try {
          const events = await getSessionEvents(sessionId);
          const availability = computeUndoRedoAvailability(events);
          set((draft) => {
            if (draft.activeSession?.id === sessionId) {
              draft.canUndo = availability.canUndo;
              draft.canRedo = availability.canRedo;
            }
          });
        } catch (error) {
          console.warn('Failed to update undo/redo availability', error);
        }
      };

      return {
        activeSession: undefined,
        isLoading: false,
        error: undefined,
        canUndo: false,
        canRedo: false,
        realtimeSubscriptions: new Map(),
        queueDiagnostics: operationQueue.getDiagnostics(),
        ...createSessionActions({
          set,
          get,
          requireActiveSession,
          refreshSessionFromEvents,
          updateUndoRedoAvailability,
          operationQueue,
          loadToken,
          getCurrentUserId,
        }),
      };
    }),
    { name: 'SessionStore' },
  ),
);
